import { Field, FieldCheckbox, FieldInput, FieldLabel, FieldSelect, FieldTextarea } from '@ibe/ui';
import { useState, type ReactNode } from 'react';
import {
  Controller,
  useWatch,
  type Control,
  type FieldErrors,
  type UseFormRegister,
  type UseFormSetValue,
} from 'react-hook-form';
import { z } from 'zod';

import { PropertyMultiSelect } from '../../../components/form/property-multi-select.js';
import { PropertySelect } from '../../../components/form/property-select.js';
import { useApiList, useApiQuery } from '../../../lib/api/hooks.js';
import { queryKeys } from '../../../lib/api/query-keys.js';
import { optionSchema } from '../../../lib/resource/module.js';
import { promotionEndpoints } from '../api/endpoints.js';
import {
  joinIds,
  PROMOTION_PLATFORM_OPTIONS,
  PROMOTION_TYPE_OPTIONS,
  splitIds,
  type PromotionFormInput,
} from '../api/schemas.js';

export interface PromotionFieldsProps {
  readonly register: UseFormRegister<PromotionFormInput>;
  readonly errors: FieldErrors<PromotionFormInput>;
  readonly control: Control<PromotionFormInput>;
  readonly setValue: UseFormSetValue<PromotionFormInput>;
  readonly mode: 'create' | 'edit';
}

function Section({
  title,
  description,
  children,
}: {
  readonly title: string;
  readonly description?: string;
  readonly children: ReactNode;
}): React.JSX.Element {
  return (
    <fieldset className="space-y-4 border-t border-border pt-4 first:border-t-0 first:pt-0">
      <legend className="sr-only">{title}</legend>
      <div className="space-y-1">
        <h2 className="text-body font-semibold">{title}</h2>
        {description === undefined ? null : (
          <p className="text-caption text-muted-foreground">{description}</p>
        )}
      </div>
      {children}
    </fieldset>
  );
}

function Row({ children }: { readonly children: ReactNode }): React.JSX.Element {
  return <div className="grid grid-cols-1 gap-4 md:grid-cols-2">{children}</div>;
}

const DAYS_OF_WEEK_OPTIONS = [
  { value: '0', label: 'Sunday' },
  { value: '1', label: 'Monday' },
  { value: '2', label: 'Tuesday' },
  { value: '3', label: 'Wednesday' },
  { value: '4', label: 'Thursday' },
  { value: '5', label: 'Friday' },
  { value: '6', label: 'Saturday' },
] as const;

function DateListInput({
  value,
  onChange,
  id,
}: {
  readonly value: string;
  readonly onChange: (value: string) => void;
  readonly id?: string | undefined;
}): React.JSX.Element {
  const dates = splitIds(value);
  const [draft, setDraft] = useState('');

  const add = (): void => {
    if (draft === '' || dates.includes(draft)) return;
    onChange(joinIds([...dates, draft].sort()));
    setDraft('');
  };

  const remove = (date: string): void => {
    onChange(joinIds(dates.filter((d) => d !== date).sort()));
  };

  return (
    <div className="space-y-2" id={id}>
      <div className="flex items-center gap-2">
        <FieldInput
          type="date"
          value={draft}
          onChange={(event) => {
            setDraft(event.currentTarget.value);
          }}
        />
        <button
          type="button"
          onClick={add}
          className="rounded-md border border-input bg-background px-3 py-2 text-sm font-medium hover:bg-muted"
        >
          Add
        </button>
      </div>
      {dates.length > 0 && (
        <div className="flex flex-wrap gap-2">
          {dates.map((date) => (
            <div
              key={date}
              className="inline-flex items-center gap-1 rounded-full bg-muted px-2 py-1 text-sm"
            >
              <span>{date}</span>
              <button
                type="button"
                onClick={() => {
                  remove(date);
                }}
                aria-label={`Remove ${date}`}
                className="ml-1 text-muted-foreground hover:text-foreground"
              >
                ×
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

const PERCENTAGE_TYPES: readonly string[] = [
  'PERCENTAGE',
  'EARLY_BIRD',
  'LAST_MINUTE',
  'LONG_STAY',
  'MEMBER',
];
const AMOUNT_TYPES: readonly string[] = ['FIXED_AMOUNT'];
const FIXED_RATE_TYPES: readonly string[] = ['FIXED_RATE'];
const STAY_PAY_TYPES: readonly string[] = ['STAY_X_PAY_Y'];
const ROOM_TYPE_PAGE_SIZE = 100;
const DROPDOWN_OPTIONS_LIMIT = 50;
const roomTypeOption = z.object({
  id: z.string(),
  name: z.string(),
  code: z.string(),
});

export function PromotionFields({
  register,
  errors,
  control,
  setValue,
}: PromotionFieldsProps): React.JSX.Element {
  const type = useWatch({ control, name: 'type' });
  const propertyIdValue = useWatch({ control, name: 'propertyId' });
  const propertyId = typeof propertyIdValue === 'string' ? propertyIdValue : '';
  const scopedPropertyId = propertyId === '' ? undefined : propertyId;

  const roomTypes = useApiList({
    resource: 'roomTypes',
    path: '/v1/room-types',
    schema: roomTypeOption,
    params: {
      page: 1,
      pageSize: ROOM_TYPE_PAGE_SIZE,
      ...(scopedPropertyId === undefined ? {} : { scope: { propertyId } }),
      fields: ['id', 'name', 'code'],
    },
    enabled: propertyId !== '',
  });
  const roomTypeOptions = (roomTypes.data?.items ?? []).map((rt) => ({
    value: rt.id,
    label: rt.name,
  }));

  const ratePlans = useApiQuery({
    queryKey: [...queryKeys.ratePlans.options({}), propertyId],
    path: '/v1/rate-plans/options',
    schema: z.array(optionSchema),
    query: {
      ...(scopedPropertyId === undefined ? {} : { propertyId }),
      limit: DROPDOWN_OPTIONS_LIMIT,
    },
    enabled: propertyId !== '',
  });
  const ratePlanOptions = (ratePlans.data ?? []).map((option) => ({
    value: option.value,
    label: option.label,
  }));

  const inclusions = useApiQuery({
    queryKey: queryKeys.inclusions.options({}),
    path: '/v1/inclusions/options',
    schema: z.array(optionSchema),
  });
  const inclusionOptions = (inclusions.data ?? []).map((option) => ({
    value: option.value,
    label: option.label,
  }));

  const promotions = useApiQuery({
    queryKey: [...queryKeys.promotions.options({}), propertyId],
    path: promotionEndpoints.options,
    schema: z.array(optionSchema),
    query: {
      ...(scopedPropertyId === undefined ? {} : { propertyId }),
      limit: DROPDOWN_OPTIONS_LIMIT,
    },
    enabled: propertyId !== '',
  });
  const promotionOptions = (promotions.data ?? []).map((option) => ({
    value: option.value,
    label: option.label,
  }));

  return (
    <div className="space-y-section">
      <Section title="Identity">
        <Row>
          <Field error={errors.code?.message} required>
            <FieldLabel>Code</FieldLabel>
            <FieldInput autoComplete="off" placeholder="SUMMER25" {...register('code')} />
          </Field>
          <Field error={errors.name?.message} required>
            <FieldLabel>Name</FieldLabel>
            <FieldInput
              autoComplete="off"
              placeholder="Summer 2025 promotion"
              {...register('name')}
            />
          </Field>
        </Row>
        <Row>
          <Field error={errors.customerFacingName?.message}>
            <FieldLabel>Customer-facing name</FieldLabel>
            <FieldInput autoComplete="off" {...register('customerFacingName')} />
          </Field>
          <Field error={errors.description?.message}>
            <FieldLabel>Description</FieldLabel>
            <FieldTextarea {...register('description')} />
          </Field>
        </Row>
      </Section>

      <Section title="Scope and exclusions">
        <Row>
          <Field error={errors.propertyId?.message}>
            <FieldLabel>Property</FieldLabel>
            <Controller
              control={control}
              name="propertyId"
              render={({ field }) => (
                <PropertySelect
                  value={field.value ?? ''}
                  onChange={(value) => {
                    const previous = field.value ?? '';
                    const changed = value !== previous;
                    field.onChange(value);
                    if (changed) {
                      setValue('roomTypeInput', '');
                      setValue('ratePlanInput', '');
                      setValue('stacksWithInput', '');
                    }
                  }}
                  error={errors.propertyId?.message}
                />
              )}
            />
          </Field>
          <Field error={errors.applyToAllRates?.message}>
            <FieldLabel>Apply to all rates</FieldLabel>
            <Controller
              control={control}
              name="applyToAllRates"
              render={({ field }) => (
                <FieldCheckbox
                  checked={field.value}
                  onCheckedChange={field.onChange}
                  onBlur={field.onBlur}
                >
                  Apply to all rates
                </FieldCheckbox>
              )}
            />
          </Field>
        </Row>
        <Row>
          <Controller
            control={control}
            name="roomTypeInput"
            render={({ field }) => (
              <PropertyMultiSelect
                value={splitIds(field.value)}
                onChange={(value) => {
                  field.onChange(joinIds(value));
                }}
                options={roomTypeOptions}
                isLoading={roomTypes.isLoading}
                label="Room types"
                description={
                  propertyId === ''
                    ? 'Choose a property to load its room types.'
                    : 'Limit this promotion to these room types.'
                }
                error={errors.roomTypeInput?.message}
                searchPlaceholder="Search room types…"
                loadingMessage="Loading room types…"
                emptyMessage={
                  propertyId === '' ? 'Choose a property first.' : 'No room types match.'
                }
              />
            )}
          />
          <Controller
            control={control}
            name="ratePlanInput"
            render={({ field }) => (
              <PropertyMultiSelect
                value={splitIds(field.value)}
                onChange={(value) => {
                  field.onChange(joinIds(value));
                }}
                options={ratePlanOptions}
                isLoading={ratePlans.isLoading}
                label="Rate plans"
                description={
                  propertyId === ''
                    ? 'Choose a property to load its rate plans.'
                    : 'Limit this promotion to these rate plans.'
                }
                error={errors.ratePlanInput?.message}
                searchPlaceholder="Search rate plans…"
                loadingMessage="Loading rate plans…"
                emptyMessage={
                  propertyId === '' ? 'Choose a property first.' : 'No rate plans match.'
                }
              />
            )}
          />
        </Row>
        <Row>
          <Field error={errors.excludedDatesInput?.message}>
            <FieldLabel>Excluded dates</FieldLabel>
            <Controller
              control={control}
              name="excludedDatesInput"
              render={({ field }) => (
                <DateListInput value={field.value ?? ''} onChange={field.onChange} />
              )}
            />
          </Field>
          <Controller
            control={control}
            name="inclusionDaysInput"
            render={({ field }) => (
              <PropertyMultiSelect
                value={splitIds(field.value)}
                onChange={(value) => {
                  field.onChange(joinIds(value));
                }}
                options={DAYS_OF_WEEK_OPTIONS}
                label="Inclusion days"
                description="Days of the week this promotion applies."
                error={errors.inclusionDaysInput?.message}
                searchPlaceholder="Search days…"
                emptyMessage="No days match."
              />
            )}
          />
        </Row>
        <Row>
          <Controller
            control={control}
            name="inclusionPackageInput"
            render={({ field }) => (
              <PropertyMultiSelect
                value={splitIds(field.value)}
                onChange={(value) => {
                  field.onChange(joinIds(value));
                }}
                options={inclusionOptions}
                isLoading={inclusions.isLoading}
                label="Inclusion packages"
                description="Packages this promotion can include."
                error={errors.inclusionPackageInput?.message}
                searchPlaceholder="Search inclusion packages…"
                loadingMessage="Loading inclusion packages…"
                emptyMessage="No inclusion packages match."
              />
            )}
          />
          <Controller
            control={control}
            name="stacksWithInput"
            render={({ field }) => (
              <PropertyMultiSelect
                value={splitIds(field.value)}
                onChange={(value) => {
                  field.onChange(joinIds(value));
                }}
                options={promotionOptions}
                isLoading={promotions.isLoading}
                label="Stacks with promotions"
                description={
                  propertyId === ''
                    ? 'Choose a property to load other promotions.'
                    : 'Other promotions this can combine with.'
                }
                error={errors.stacksWithInput?.message}
                searchPlaceholder="Search promotions…"
                loadingMessage="Loading promotions…"
                emptyMessage={
                  propertyId === '' ? 'Choose a property first.' : 'No promotions match.'
                }
              />
            )}
          />
        </Row>
      </Section>

      <Section title="Discount logic">
        <Row>
          <Field error={errors.type?.message} required>
            <FieldLabel>Type</FieldLabel>
            <Controller
              control={control}
              name="type"
              render={({ field }) => (
                <FieldSelect
                  value={field.value}
                  onValueChange={field.onChange}
                  onBlur={field.onBlur}
                  options={PROMOTION_TYPE_OPTIONS}
                  placeholder="Select a promotion type"
                  searchable
                />
              )}
            />
          </Field>
          {(AMOUNT_TYPES.includes(type) || FIXED_RATE_TYPES.includes(type)) && (
            <Field error={errors.currency?.message} required>
              <FieldLabel>Currency</FieldLabel>
              <FieldInput autoComplete="off" maxLength={3} {...register('currency')} />
            </Field>
          )}
        </Row>
        <Row>
          <Field error={errors.priority?.message} required>
            <FieldLabel>Priority</FieldLabel>
            <FieldInput inputMode="numeric" {...register('priority')} />
          </Field>
          {PERCENTAGE_TYPES.includes(type) && (
            <Field error={errors.percentage?.message}>
              <FieldLabel>Percentage (%)</FieldLabel>
              <FieldInput inputMode="decimal" {...register('percentage')} />
            </Field>
          )}
        </Row>
        <Row>
          {AMOUNT_TYPES.includes(type) && (
            <Field error={errors.amount?.message}>
              <FieldLabel>Amount off</FieldLabel>
              <FieldInput inputMode="decimal" {...register('amount')} />
            </Field>
          )}
          {AMOUNT_TYPES.includes(type) && (
            <Field error={errors.maxDiscount?.message}>
              <FieldLabel>Maximum discount</FieldLabel>
              <FieldInput inputMode="decimal" {...register('maxDiscount')} />
            </Field>
          )}
        </Row>
        <Row>
          {FIXED_RATE_TYPES.includes(type) && (
            <Field error={errors.fixedRate?.message}>
              <FieldLabel>Fixed rate</FieldLabel>
              <FieldInput inputMode="decimal" {...register('fixedRate')} />
            </Field>
          )}
          <Field error={errors.minNights?.message}>
            <FieldLabel>Minimum nights</FieldLabel>
            <FieldInput inputMode="numeric" {...register('minNights')} />
          </Field>
        </Row>
        <Row>
          {STAY_PAY_TYPES.includes(type) && (
            <Field error={errors.stayNights?.message}>
              <FieldLabel>Stay nights</FieldLabel>
              <FieldInput inputMode="numeric" {...register('stayNights')} />
            </Field>
          )}
          {STAY_PAY_TYPES.includes(type) && (
            <Field error={errors.payNights?.message}>
              <FieldLabel>Pay nights</FieldLabel>
              <FieldInput inputMode="numeric" {...register('payNights')} />
            </Field>
          )}
        </Row>
        <Row>
          <Field error={errors.minAdvanceDays?.message}>
            <FieldLabel>Minimum advance days</FieldLabel>
            <FieldInput inputMode="numeric" {...register('minAdvanceDays')} />
          </Field>
          <Field error={errors.maxAdvanceDays?.message}>
            <FieldLabel>Maximum advance days</FieldLabel>
            <FieldInput inputMode="numeric" {...register('maxAdvanceDays')} />
          </Field>
        </Row>
      </Section>

      <Section title="Booking and stay windows">
        <Row>
          <Field error={errors.bookableFrom?.message}>
            <FieldLabel>Bookable from</FieldLabel>
            <FieldInput type="date" {...register('bookableFrom')} />
          </Field>
          <Field error={errors.bookableTo?.message}>
            <FieldLabel>Bookable to</FieldLabel>
            <FieldInput type="date" {...register('bookableTo')} />
          </Field>
        </Row>
        <Row>
          <Field error={errors.stayFrom?.message}>
            <FieldLabel>Stay from</FieldLabel>
            <FieldInput type="date" {...register('stayFrom')} />
          </Field>
          <Field error={errors.stayTo?.message}>
            <FieldLabel>Stay to</FieldLabel>
            <FieldInput type="date" {...register('stayTo')} />
          </Field>
        </Row>
      </Section>

      <Section title="Visibility and platform">
        <Row>
          <Field error={errors.platform?.message} required>
            <FieldLabel>Platform</FieldLabel>
            <Controller
              control={control}
              name="platform"
              render={({ field }) => (
                <FieldSelect
                  value={field.value}
                  onValueChange={field.onChange}
                  onBlur={field.onBlur}
                  options={PROMOTION_PLATFORM_OPTIONS}
                  placeholder="Select a platform"
                  searchable
                />
              )}
            />
          </Field>
          <Field error={errors.pmsPromoCode?.message}>
            <FieldLabel>PMS promo code</FieldLabel>
            <FieldInput autoComplete="off" {...register('pmsPromoCode')} />
          </Field>
        </Row>
        <Row>
          <Field error={errors.visibilityFrom?.message}>
            <FieldLabel>Visibility from</FieldLabel>
            <FieldInput type="date" {...register('visibilityFrom')} />
          </Field>
          <Field error={errors.visibilityTo?.message}>
            <FieldLabel>Visibility to</FieldLabel>
            <FieldInput type="date" {...register('visibilityTo')} />
          </Field>
        </Row>
      </Section>

      <Section title="Flags">
        <div className="flex flex-wrap gap-4">
          <Field error={errors.enabled?.message}>
            <Controller
              control={control}
              name="enabled"
              render={({ field }) => (
                <FieldCheckbox
                  checked={field.value}
                  onCheckedChange={field.onChange}
                  onBlur={field.onBlur}
                >
                  Enabled
                </FieldCheckbox>
              )}
            />
          </Field>
          <Field error={errors.combinable?.message}>
            <Controller
              control={control}
              name="combinable"
              render={({ field }) => (
                <FieldCheckbox
                  checked={field.value}
                  onCheckedChange={field.onChange}
                  onBlur={field.onBlur}
                >
                  Combinable
                </FieldCheckbox>
              )}
            />
          </Field>
          <Field error={errors.exclusive?.message}>
            <Controller
              control={control}
              name="exclusive"
              render={({ field }) => (
                <FieldCheckbox
                  checked={field.value}
                  onCheckedChange={field.onChange}
                  onBlur={field.onBlur}
                >
                  Exclusive
                </FieldCheckbox>
              )}
            />
          </Field>
          <Field error={errors.promoCodeRequired?.message}>
            <Controller
              control={control}
              name="promoCodeRequired"
              render={({ field }) => (
                <FieldCheckbox
                  checked={field.value}
                  onCheckedChange={field.onChange}
                  onBlur={field.onBlur}
                >
                  Promo code required
                </FieldCheckbox>
              )}
            />
          </Field>
          <Field error={errors.showOnlyThisPromo?.message}>
            <Controller
              control={control}
              name="showOnlyThisPromo"
              render={({ field }) => (
                <FieldCheckbox
                  checked={field.value}
                  onCheckedChange={field.onChange}
                  onBlur={field.onBlur}
                >
                  Show only this promo
                </FieldCheckbox>
              )}
            />
          </Field>
          <Field error={errors.continuesPromotion?.message}>
            <Controller
              control={control}
              name="continuesPromotion"
              render={({ field }) => (
                <FieldCheckbox
                  checked={field.value}
                  onCheckedChange={field.onChange}
                  onBlur={field.onBlur}
                >
                  Continues promotion
                </FieldCheckbox>
              )}
            />
          </Field>
          <Field error={errors.excludedEnabled?.message}>
            <Controller
              control={control}
              name="excludedEnabled"
              render={({ field }) => (
                <FieldCheckbox
                  checked={field.value}
                  onCheckedChange={field.onChange}
                  onBlur={field.onBlur}
                >
                  Exclusion window enabled
                </FieldCheckbox>
              )}
            />
          </Field>
          <Field error={errors.promotionLimitTime?.message}>
            <Controller
              control={control}
              name="promotionLimitTime"
              render={({ field }) => (
                <FieldCheckbox
                  checked={field.value}
                  onCheckedChange={field.onChange}
                  onBlur={field.onBlur}
                >
                  Limit promotion time
                </FieldCheckbox>
              )}
            />
          </Field>
        </div>
      </Section>
    </div>
  );
}
